import { SUIT_SYMBOL, SUITS, parseCard } from '../lib/poker'
import type { Card, Rank, Suit } from '../lib/types'
import { t } from '../i18n/strings'
import { getSessionHandIndex } from '../state'
import { clear, el } from './dom'

/**
 * Persists across parent re-renders so suit choice stays while picking.
 * Softwise M10d: starts unselected (null) — the four suits carry equal
 * weight until the user taps one; nothing is pre-chosen for them.
 */
let activeSuit: Suit | null = null

/** Rank tapped before any suit (either order works: rank → suit). */
let pendingRank: Rank | null = null

/** Most-recently-used suit order — empty until the user picks a suit. */
let suitMru: Suit[] = []

/** New hand → the picker starts unselected again. */
let pickerHandIndex: number | null = null
function syncPickerHand(): void {
  const idx = getSessionHandIndex()
  if (pickerHandIndex !== idx) {
    pickerHandIndex = idx
    activeSuit = null
    pendingRank = null
    suitMru = []
  }
}

function bumpSuitMru(suit: Suit): void {
  suitMru = [suit, ...suitMru.filter((s) => s !== suit)]
}

function suitClass(suit: Suit): string {
  if (suit === 's') return 'suitS'
  if (suit === 'h') return 'suitH'
  if (suit === 'd') return 'suitD'
  return 'suitC'
}

export function playingCard(opts: {
  card: Card | null
  large?: boolean
  emptyLabel?: string
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
}): HTMLElement {
  const interactive = typeof opts.onClick === 'function'
  const className = [
    'playingCard',
    opts.large ? 'playingCardLarge' : '',
    !opts.card ? 'playingCardEmpty' : '',
    opts.selected ? 'playingCardSelected' : '',
    opts.disabled ? 'playingCardDisabled' : '',
    opts.card ? suitClass(parseCard(opts.card).suit) : '',
    interactive ? '' : 'playingCardStatic',
  ]
    .filter(Boolean)
    .join(' ')

  const label = opts.card
    ? (() => {
        const { rank, suit } = parseCard(opts.card)
        return `${rank}${SUIT_SYMBOL[suit]}`
      })()
    : (opts.emptyLabel ?? '?')

  const node = el(interactive ? 'button' : 'div', {
    className,
    attrs: interactive
      ? {
          type: 'button',
          disabled: opts.disabled ? true : undefined,
          'aria-pressed': opts.selected ? 'true' : 'false',
          'aria-label': label,
        }
      : {
          role: 'img',
          'aria-label': label,
        },
    on: interactive ? { click: () => opts.onClick?.() } : undefined,
  })

  if (opts.card) {
    const { rank, suit } = parseCard(opts.card)
    node.append(el('span', { text: rank }), el('span', { text: SUIT_SYMBOL[suit] }))
  } else {
    node.append(el('span', { text: opts.emptyLabel ?? '?' }))
  }

  return node
}

/** High → low, fits a 4-column thumb pad (A…2). */
const RANK_PAD: Rank[] = [
  'A',
  'K',
  'Q',
  'J',
  'T',
  '9',
  '8',
  '7',
  '6',
  '5',
  '4',
  '3',
  '2',
]

/**
 * Portrait thumb picker: suit chips + large rank pad.
 * Never lays out 13 ranks in a single horizontal row.
 * Keeps activeSuit across picks (auto-advance does not reset suit).
 */
export function cardGrid(opts: {
  used: Set<Card>
  onPick: (card: Card) => void
}): HTMLDivElement {
  const root = el('div', { className: 'cardPicker' })

  syncPickerHand()

  const paint = (): void => {
    const ui = t()
    clear(root)
    const suitSel = activeSuit

    // Softwise M10d: the sticky row keeps its height before a suit is picked
    // (hidden, not removed) so the pad never shifts when a suit is chosen.
    const sticky = el(
      'div',
      {
        className: ['suitStickyRow', suitSel ? '' : 'suitStickyRowIdle'].filter(Boolean).join(' '),
        attrs: suitSel ? {} : { 'aria-hidden': 'true' },
      },
      suitSel
        ? [
            el('span', {
              className: ['suitStickyChip', suitClass(suitSel), 'suitStickyChipActive'].join(' '),
              attrs: { title: ui.suitStickyChip(ui.suit[suitSel]) },
              text: `${SUIT_SYMBOL[suitSel]} ${ui.suit[suitSel]}`,
            }),
            el('span', { className: 'suitStickyHint', text: ui.suitStickyHint }),
          ]
        : [
            // Same box as the chip + hint, invisible: reserves the row height.
            el('span', { className: 'suitStickyChip', text: `${SUIT_SYMBOL.s} ${ui.suit.s}` }),
            el('span', { className: 'suitStickyHint', text: ui.suitStickyHint }),
          ],
    )

    const orderedSuits = [
      ...suitMru.filter((s) => SUITS.includes(s)),
      ...SUITS.filter((s) => !suitMru.includes(s)),
    ]

    const suitRow = el('div', {
      className: 'suitTabs',
      attrs: { role: 'tablist', 'aria-label': ui.suitPickerLabel },
    })
    for (const suit of orderedSuits) {
      const selected = suitSel === suit
      const isMru = suitMru[0] === suit
      // With a rank already tapped, a suit whose card is taken cannot finish it.
      const blocked = pendingRank != null && opts.used.has(`${pendingRank}${suit}` as Card)
      suitRow.append(
        el('button', {
          className: [
            'suitTab',
            suitClass(suit),
            selected ? 'suitTabActive' : '',
            isMru ? 'suitTabMru' : '',
          ]
            .filter(Boolean)
            .join(' '),
          attrs: {
            type: 'button',
            role: 'tab',
            disabled: blocked ? true : undefined,
            'aria-selected': selected ? 'true' : 'false',
            'aria-label': isMru
              ? `${ui.suit[suit]} (${ui.suitMruLabel})`
              : ui.suit[suit],
            title: isMru
              ? `${ui.suit[suit]} · ${ui.suitMruLabel}`
              : ui.suit[suit],
          },
          on: {
            click: () => {
              activeSuit = suit
              bumpSuitMru(suit)
              const rank = pendingRank
              pendingRank = null
              if (rank) {
                const card = `${rank}${suit}` as Card
                if (!opts.used.has(card)) {
                  opts.onPick(card)
                  return
                }
              }
              paint()
            },
          },
        }, [
          el('span', { className: 'suitTabSymbol', text: SUIT_SYMBOL[suit] }),
          el('span', { className: 'suitTabName', text: ui.suit[suit] }),
        ]),
      )
    }

    const rankPad = el('div', {
      className: 'rankPad',
      attrs: { role: 'group', 'aria-label': ui.rankPickerLabel },
    })
    for (const rank of RANK_PAD) {
      const card = suitSel ? (`${rank}${suitSel}` as Card) : null
      // No suit yet: a rank is only unavailable when all four cards are used.
      const taken = card
        ? opts.used.has(card)
        : SUITS.every((su) => opts.used.has(`${rank}${su}` as Card))
      const pending = !suitSel && pendingRank === rank
      rankPad.append(
        el('button', {
          className: [
            'rankBtn',
            suitSel ? suitClass(suitSel) : 'rankBtnNeutral',
            pending ? 'rankBtnPending' : '',
            taken ? 'rankBtnDisabled' : '',
          ]
            .filter(Boolean)
            .join(' '),
          attrs: {
            type: 'button',
            disabled: taken ? true : undefined,
            'aria-label': suitSel ? `${rank}${SUIT_SYMBOL[suitSel]}` : rank,
            'aria-pressed': suitSel ? undefined : pending ? 'true' : 'false',
          },
          text: rank,
          on: {
            click: () => {
              if (taken) return
              if (card && suitSel) {
                // Keep suit sticky; bump MRU so next paint shows recent first.
                bumpSuitMru(suitSel)
                opts.onPick(card)
                return
              }
              // No suit yet: hold the rank until a suit is tapped.
              pendingRank = pendingRank === rank ? null : rank
              paint()
            },
          },
        }),
      )
    }

    root.append(sticky, suitRow, rankPad)
  }

  paint()
  return root
}
